/**
 * Category Curation Settings
 *
 * Three-state category management for the curated filter:
 * - Included: shown when curated filter is ON
 * - Excluded: deliberately hidden (admin reviewed and rejected)
 * - New: not yet reviewed (category exists in DB but not in curation table)
 *
 * Uses /places/categories for ALL distinct categories and
 * /settings/category-curation for the curation state.
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Typography, Input, Alert, Select, Tag, Radio } from 'antd';
import { SearchOutlined, SaveOutlined, CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useAllCategories } from '@/hooks/usePlaces';
import { useCategoryCuration, useUpdateCategoryCuration } from '@/hooks/useSettings';

const { Text } = Typography;

type CurationStatus = 'included' | 'excluded' | 'new';

interface CategoryRow {
  category: string;
  status: CurationStatus;
}

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New (unreviewed)' },
  { value: 'included', label: 'Included' },
  { value: 'excluded', label: 'Excluded' },
];

export function CategoryVisibility() {
  const { data: allCategories = [], isLoading: categoriesLoading } = useAllCategories();
  const { data: curationEntries = [], isLoading: curationLoading } = useCategoryCuration();
  const updateCuration = useUpdateCategoryCuration();

  // Local state: map of category → status
  const [localState, setLocalState] = useState<Map<string, CurationStatus>>(new Map());
  const [dirty, setDirty] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState(50);

  // Sync from server: build the local map from curation entries
  useEffect(() => {
    if (curationEntries) {
      const map = new Map<string, CurationStatus>();
      for (const entry of curationEntries) {
        map.set(entry.category, entry.status);
      }
      setLocalState(map);
      setDirty(false);
    }
  }, [curationEntries]);

  // Get the status for a category (defaults to 'new' if not in map)
  const getStatus = (category: string): CurationStatus => {
    return localState.get(category) || 'new';
  };

  const handleStatusChange = (category: string, status: CurationStatus) => {
    setLocalState((prev) => {
      const next = new Map(prev);
      if (status === 'new') {
        next.delete(category);
      } else {
        next.set(category, status);
      }
      return next;
    });
    setDirty(true);
  };

  // Bulk actions on currently visible (filtered) categories
  const handleBulkAction = (status: CurationStatus) => {
    setLocalState((prev) => {
      const next = new Map(prev);
      for (const row of filteredData) {
        if (status === 'new') {
          next.delete(row.category);
        } else {
          next.set(row.category, status);
        }
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = () => {
    const entries = Array.from(localState.entries())
      .filter(([, status]) => status === 'included' || status === 'excluded')
      .map(([category, status]) => ({ category, status: status as 'included' | 'excluded' }));
    updateCuration.mutate(entries);
    setDirty(false);
  };

  // Build table data: merge all categories with their curation status
  const allData: CategoryRow[] = useMemo(() => {
    return allCategories.map((cat) => ({
      category: cat,
      status: getStatus(cat),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategories, localState]);

  // Apply search + status filter
  const filteredData = useMemo(() => {
    let data = allData;
    if (searchFilter) {
      const lower = searchFilter.toLowerCase();
      data = data.filter((row) => row.category.toLowerCase().includes(lower));
    }
    if (statusFilter !== 'all') {
      data = data.filter((row) => row.status === statusFilter);
    }
    return data;
  }, [allData, searchFilter, statusFilter]);

  // Counts
  const counts = useMemo(() => {
    let included = 0;
    let excluded = 0;
    let newCount = 0;
    for (const row of allData) {
      if (row.status === 'included') included++;
      else if (row.status === 'excluded') excluded++;
      else newCount++;
    }
    return { included, excluded, new: newCount, total: allData.length };
  }, [allData]);

  const columns = [
    {
      title: 'Status',
      dataIndex: 'category',
      key: 'status',
      width: 200,
      render: (_: string, record: CategoryRow) => (
        <Radio.Group
          size="small"
          value={record.status}
          onChange={(e) => handleStatusChange(record.category, e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button
            value="included"
            style={record.status === 'included' ? { background: '#52c41a', borderColor: '#52c41a', color: '#fff' } : undefined}
          >
            <CheckCircleOutlined />
          </Radio.Button>
          <Radio.Button
            value="new"
            style={record.status === 'new' ? { background: '#faad14', borderColor: '#faad14', color: '#fff' } : undefined}
          >
            <QuestionCircleOutlined />
          </Radio.Button>
          <Radio.Button
            value="excluded"
            style={record.status === 'excluded' ? { background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } : undefined}
          >
            <CloseCircleOutlined />
          </Radio.Button>
        </Radio.Group>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string, record: CategoryRow) => (
        <Text delete={record.status === 'excluded'} type={record.status === 'excluded' ? 'secondary' : record.status === 'new' ? 'warning' : undefined}>
          {category}
        </Text>
      ),
    },
  ];

  const isLoading = categoriesLoading || curationLoading;

  return (
    <div>
      <Alert
        message="Curate which categories appear in the filtered view"
        description={
          <span>
            <Tag color="green"><CheckCircleOutlined /> Included</Tag> shown when curated filter is ON.{' '}
            <Tag color="red"><CloseCircleOutlined /> Excluded</Tag> deliberately hidden.{' '}
            <Tag color="orange"><QuestionCircleOutlined /> New</Tag> not yet reviewed — new categories appear here automatically.
          </span>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Space wrap>
            <Input
              placeholder="Filter categories..."
              prefix={<SearchOutlined />}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
              style={{ width: 170 }}
            />
            <Button size="small" onClick={() => handleBulkAction('included')} style={{ color: '#52c41a', borderColor: '#52c41a' }}>
              Include Visible
            </Button>
            <Button size="small" onClick={() => handleBulkAction('excluded')} style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}>
              Exclude Visible
            </Button>
          </Space>
          <Space>
            <Text type="secondary">
              <Tag color="green">{counts.included}</Tag>
              <Tag color="red">{counts.excluded}</Tag>
              {counts.new > 0 && <Tag color="orange">{counts.new} new</Tag>}
              of {counts.total}
            </Text>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={updateCuration.isPending}
              disabled={!dirty}
            >
              Save
            </Button>
          </Space>
        </Space>
      </Card>

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="category"
        loading={isLoading}
        size="small"
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
      />
    </div>
  );
}

export default CategoryVisibility;
